/* This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 */

import { DeviceDetector } from "./DeviceDetector";
import { EventManager } from "./EventManager";

/**
 * Platform-specific quirks that affect keyboard behavior
 */
interface PlatformQuirks {
  keyupUnreliable: boolean;
  altIsTypableOnly: boolean;
  capsLockKeyupUnreliable: boolean;
}

/**
 * Represents a keyboard event with additional metadata
 */
class KeyEvent {
  public keyCode: number;
  public key: string;
  public location: number;
  public timestamp: number;
  public keysym: number | null = null;
  public reliable = false;
  public keyupReliable = true;

  constructor(event: KeyboardEvent) {
    this.keyCode = event.keyCode || event.which || 0;
    this.key = event.key || "";
    this.location = this.getEventLocation(event);
    this.timestamp = Date.now();
  }

  private getEventLocation(event: KeyboardEvent): number {
    if ("location" in event) return event.location;
    if ("keyLocation" in event) return (event as any).keyLocation;
    return 0;
  }

  getAge(): number {
    return Date.now() - this.timestamp;
  }
}

class KeydownEvent extends KeyEvent {
  constructor(event: KeyboardEvent, quirks: PlatformQuirks) {
    super(event);

    // Try to determine keysym from key identifier or keycode
    this.keysym =
      this.getKeysymFromKey(this.key, this.location) ||
      this.getKeysymFromKeycode(this.keyCode, this.location);

    // Set reliability based on whether this is a printable character
    if (this.keysym && !this.isPrintable(this.keysym)) {
      this.reliable = true;
    }

    // Handle platform-specific keyup reliability
    this.keyupReliable = !quirks.keyupUnreliable;

    // Handle specific unreliable cases
    if (this.keysym === 0xffe5 && quirks.capsLockKeyupUnreliable) {
      this.keyupReliable = false;
    }
  }

  private isPrintable(keysym: number): boolean {
    return (
      (keysym >= 0x00 && keysym <= 0xff) || (keysym & 0xffff0000) === 0x01000000
    );
  }

  private getKeysymFromKey(key: string, location: number): number | null {
    // Handle single character keys
    if (key.length === 1) {
      return key.charCodeAt(0);
    }

    // Handle special keys
    const keysyms = Keyboard.SPECIAL_KEYS[key];
    return keysyms ? keysyms[location] || keysyms[0] : null;
  }

  private getKeysymFromKeycode(
    keyCode: number,
    location: number,
  ): number | null {
    const keysyms = Keyboard.KEYCODE_MAP[keyCode];
    return keysyms ? keysyms[location] || keysyms[0] : null;
  }
}

class KeypressEvent extends KeyEvent {
  constructor(event: KeyboardEvent) {
    super(event);

    if (event.key && event.key.length === 1) {
      const codepoint = event.key.charCodeAt(0);
      this.keysym = this.getKeysymFromCharcode(codepoint);
    } else {
      this.keysym = this.getKeysymFromCharcode(this.keyCode);
    }

    this.reliable = true;
  }

  private getKeysymFromCharcode(codepoint: number): number {
    // Control characters
    if (this.isControlCharacter(codepoint)) {
      return 0xff00 | codepoint;
    }

    // ASCII characters (0x0000 to 0x00FF)
    if (codepoint >= 0x0000 && codepoint <= 0x00ff) {
      return codepoint;
    }

    // Unicode characters
    if (codepoint >= 0x0100 && codepoint <= 0x10ffff) {
      return 0x01000000 | codepoint;
    }

    return codepoint;
  }

  private isControlCharacter(codepoint: number): boolean {
    return codepoint <= 0x1f || (codepoint >= 0x7f && codepoint <= 0x9f);
  }
}

class KeyupEvent extends KeyEvent {
  constructor(
    event: KeyboardEvent,
    recentKeysyms: Map<number, number>,
    pressedKeys: Set<number>,
  ) {
    super(event);

    this.keysym =
      this.getKeysymFromKeycode(this.keyCode, this.location) ||
      this.getKeysymFromKey(this.key, this.location);

    // Fall back to recent keysym if current one doesn't seem right
    if (!this.keysym || !this.isKeyCurrentlyPressed(this.keysym, pressedKeys)) {
      this.keysym = recentKeysyms.get(this.keyCode) || this.keysym;
    }

    this.reliable = true;
  }

  private isKeyCurrentlyPressed(
    keysym: number,
    pressedKeys: Set<number>,
  ): boolean {
    return pressedKeys.has(keysym);
  }

  private getKeysymFromKey(key: string, _: number): number | null {
    // Reuse logic from KeydownEvent
    if (key.length === 1) {
      return key.charCodeAt(0);
    }
    // Add special key handling as needed
    return null;
  }

  private getKeysymFromKeycode(
    keyCode: number,
    location: number,
  ): number | null {
    // Reuse logic from KeydownEvent
    const keysyms = Keyboard.KEYCODE_MAP[keyCode];
    return keysyms ? keysyms[location] || keysyms[0] : null;
  }
}

/**
 * Enhanced keyboard event handler with production-ready features
 */
export class Keyboard {
  public onkeydown: ((keysym: number) => void) | null = null;
  public onkeyup: ((keysym: number) => void) | null = null;
  public onunicode: ((char: string) => void) | null = null;
  private isDebug = false; // Debug logging disabled by default for performance

  private element: Window | HTMLElement;
  private quirks: PlatformQuirks;
  private eventLog: KeyEvent[] = [];
  private pressed: Set<number> = new Set();
  private recentKeysyms: Map<number, number> = new Map();
  private keyRepeatTimeout: number | null = null;
  private keyRepeatInterval: number | null = null;
  private isComposing = false;
  private lastViewportHeight: number;
  private capsLockOn = false; // Track if CapsLock is currently ON
  private lastComposedText = ""; // Track last composition to prevent double-typing
  private readonly eventManager: EventManager;

  // TV platform debouncing for special characters
  private lastTypedText = "";
  private lastTypeTime = 0;
  private readonly typeDebounceMs = 100; // Debounce rapid duplicate typing on TV
  private readonly isTV: boolean;
  private readonly isMobile: boolean;

  // Keys that should not repeat when held
  private readonly noRepeatKeys = new Set([
    0xfe03, 0xffe1, 0xffe2, 0xffe3, 0xffe4, 0xffe5, 0xffe7, 0xffe8, 0xffe9,
    0xffea, 0xffeb, 0xffec,
  ]);

  // Lookup tables moved to class level for performance
  private static readonly SPECIAL_CHARACTERS: { [char: number]: number } = {
    0x21: 0x31, // ! -> 1
    0x40: 0x32, // @ -> 2
    0x23: 0x33, // # -> 3
    0x24: 0x34, // $ -> 4
    0x25: 0x35, // % -> 5
    0x5e: 0x36, // ^ -> 6
    0x26: 0x37, // & -> 7
    0x2a: 0x38, // * -> 8
    0x28: 0x39, // ( -> 9
    0x29: 0x30, // ) -> 0
    0x5f: 0x2d, // _ -> -
    0x2b: 0x3d, // + -> =
    0x7b: 0x5b, // { -> [
    0x7d: 0x5d, // } -> ]
    0x7c: 0x5c, // | -> \
    0x3a: 0x3b, // : -> ;
    0x22: 0x27, // " -> '
    0x3c: 0x2c, // < -> ,
    0x3e: 0x2e, // > -> .
    0x3f: 0x2f, // ? -> /
    0x7e: 0x60, // ~ -> `
    // Currency / special symbols — no Shift combo; mapped to Unicode keysym → sent directly
    0x00a2: 0x010000a2, // ¢ (Cent sign)
    0x00a3: 0x010000a3, // £ (Pound sign)
    0x00a5: 0x010000a5, // ¥ (Yen/Yuan sign)
    0x00a9: 0x010000a9, // © (Copyright symbol)
    0x00ae: 0x010000ae, // ® (Registered trademark symbol)
    0x00b0: 0x010000b0, // ° (Degree symbol)
    0x20ac: 0x010020ac, // € — keyboard event path (getKeysymFromKey returns 0x20AC)
    0x010020ac: 0x010020ac, // € — type() path (keysymFromCharcode returns 0x01000000|0x20AC)
    0x2022: 0x01002022, // • — keyboard event path
    0x01002022: 0x01002022, // • — type() path
  };

  public static readonly SPECIAL_KEYS: { [key: string]: number[] } = {
    Escape: [0xff1b],
    Tab: [0xff09],
    Enter: [0xff0d],
    Backspace: [0xff08],
    Delete: [0xffff],
    Insert: [0xff63],
    Home: [0xff50],
    End: [0xff57],
    PageUp: [0xff55],
    PageDown: [0xff56],
    ArrowLeft: [0xff51],
    ArrowUp: [0xff52],
    ArrowRight: [0xff53],
    ArrowDown: [0xff54],
    F1: [0xffbe],
    F2: [0xffbf],
    F3: [0xffc0],
    F4: [0xffc1],
    F5: [0xffc2],
    F6: [0xffc3],
    F7: [0xffc4],
    F8: [0xffc5],
    F9: [0xffc6],
    F10: [0xffc7],
    F11: [0xffc8],
    F12: [0xffc9],
    Shift: [0xffe1, 0xffe1, 0xffe2],
    Control: [0xffe3, 0xffe3, 0xffe4],
    Alt: [0xffe9, 0xffe9, 0xffea],
    Meta: [0xffeb, 0xffeb, 0xffec],
    CapsLock: [0xffe5],
    NumLock: [0xff7f],
    ScrollLock: [0xff14],
  };

  public static readonly KEYCODE_MAP: { [code: number]: number[] } = {
    8: [0xff08], // backspace
    9: [0xff09], // tab
    13: [0xff0d], // enter
    16: [0xffe1, 0xffe1, 0xffe2], // shift
    17: [0xffe3, 0xffe3, 0xffe4], // ctrl
    18: [0xffe9, 0xffe9, 0xffea], // alt
    27: [0xff1b], // escape
    32: [0x0020], // space
    37: [0xff51], // left arrow
    38: [0xff52], // up arrow
    39: [0xff53], // right arrow
    40: [0xff54], // down arrow
    46: [0xffff], // delete
    112: [0xffbe], // F1
    113: [0xffbf], // F2
    114: [0xffc0], // F3
    115: [0xffc1], // F4
    116: [0xffc2], // F5
    117: [0xffc3], // F6
    118: [0xffc4], // F7
    119: [0xffc5], // F8
    120: [0xffc6], // F9
    121: [0xffc7], // F10
    122: [0xffc8], // F11
    123: [0xffc9], // F12
  };

  private static readonly SPECIAL_KEY_NAMES: { [key: number]: string } = {
    0xff08: "Backspace",
    0xff09: "Tab",
    0xff0d: "Enter",
    0xff1b: "Escape",
    0xff51: "←",
    0xff52: "↑",
    0xff53: "→",
    0xff54: "↓",
    0xffe1: "Shift(L)",
    0xffe2: "Shift(R)",
    0xffe3: "Ctrl(L)",
    0xffe4: "Ctrl(R)",
    0xffe9: "Alt(L)",
    0xffea: "Alt(R)",
    0xffe5: "CapsLock",
    0xffff: "Delete",
    0x0020: "Space",
  };

  constructor(element: Window | HTMLElement, isTV = false) {
    this.element = element;
    this.isTV = isTV;
    this.isMobile = DeviceDetector.isMobileDevice(); // Cache device detection
    this.quirks = this.detectPlatformQuirks();
    this.lastViewportHeight =
      window.visualViewport?.height || window.innerHeight;
    this.eventManager = new EventManager();
    // this.setupEventListeners();
  }

  private log(prefix: string, log?: unknown) {
    if (!this.isDebug) return;

    console.log(`[Keyboard] - ${prefix}`, log);
  }

  /**
   * Enable or disable debug logging
   * @param enabled - Whether to enable debug logging
   */
  public setDebugMode(enabled: boolean): void {
    this.isDebug = enabled;
    if (enabled) {
      console.log("[Keyboard] Debug mode enabled");
    }
  }

  /**
   * Character event handler
   */
  private handleCharacterEvent(
    keysym: number,
    event: KeydownEvent,
  ): KeydownEvent {
    const isUppercaseLetter = keysym >= 0x41 && keysym <= 0x5a; // A-Z
    const isLowercaseLetter = keysym >= 0x61 && keysym <= 0x7a; // a-z
    const isSpecialCharacter = Object.prototype.hasOwnProperty.call(
      Keyboard.SPECIAL_CHARACTERS,
      keysym,
    );

    // Handle special characters universally
    if (isSpecialCharacter) {
      return this.handleSpecialCharacter(keysym, event);
    }

    // Handle letters based on platform
    if (isUppercaseLetter || isLowercaseLetter) {
      return this.isMobile
        ? this.handleMobileLetter(keysym, event, isUppercaseLetter)
        : this.handleDesktopLetter(keysym, event, isUppercaseLetter);
    }

    // Default handling
    return this.handleDefaultKey(keysym, event);
  }

  private handleSpecialCharacter(
    keysym: number,
    event: KeydownEvent,
  ): KeydownEvent {
    const baseKeysym = Keyboard.SPECIAL_CHARACTERS[keysym];

    // Unicode symbols (0x01000000+) always inject via onunicode regardless of
    // Shift state — physical keyboards can produce these with Shift held (e.g.
    // Shift+4 = € on some layouts), but the character intent is unambiguous.
    if ((baseKeysym & 0xff000000) === 0x01000000) {
      const unicodeChar = String.fromCodePoint(baseKeysym & 0x00ffffff);
      if (this.onunicode) {
        this.onunicode(unicodeChar);
      } else {
        this.pressKey(baseKeysym);
        this.releaseKey(baseKeysym);
      }
      this.recentKeysyms.set(event.keyCode, baseKeysym);
      return event;
    }

    const shiftHeld = this.pressed.has(0xffe1) || this.pressed.has(0xffe2);

    if (!shiftHeld) {
      this.log(
        `[Special] Artificial Shift + base: 0x${baseKeysym.toString(16)}`,
      );

      this.sendShiftedKey(baseKeysym);
      this.recentKeysyms.set(event.keyCode, baseKeysym);
      return event;
    }

    return this.handleDefaultKey(keysym, event);
  }

  private handleMobileLetter(
    keysym: number,
    event: KeydownEvent,
    isUppercase: boolean,
  ): KeydownEvent {
    if (isUppercase) {
      // Mobile uppercase: artificial Shift + lowercase
      const lowercaseKeysym = keysym + 32;
      this.log(
        `[Mobile] Artificial Shift + lowercase: 0x${lowercaseKeysym.toString(
          16,
        )}`,
      );

      this.sendShiftedKey(lowercaseKeysym);
      this.recentKeysyms.set(event.keyCode, lowercaseKeysym);
      return event;
    }

    // Lowercase: send as-is
    return this.handleDefaultKey(keysym, event);
  }

  private handleDesktopLetter(
    keysym: number,
    event: KeydownEvent,
    isUppercase: boolean,
  ): KeydownEvent {
    const shiftHeld = this.pressed.has(0xffe1) || this.pressed.has(0xffe2);

    // uppercase letter without Shift → Caps Lock is ON
    // lowercase letter without Shift → Caps Lock is OFF
    if (isUppercase && !shiftHeld) {
      if (!this.capsLockOn) {
        this.log("[Desktop] Detected Caps Lock ON - syncing with server");
        this.capsLockOn = true;
        // Send Caps Lock toggle to server to sync state
        if (this.onkeydown && this.onkeyup) {
          this.onkeydown(0xffe5);
          this.onkeyup(0xffe5);
        }
      }
    } else if (!isUppercase && !shiftHeld) {
      if (this.capsLockOn) {
        this.log("[Desktop] Detected Caps Lock OFF - syncing with server");
        this.capsLockOn = false;
        // Send Caps Lock toggle to server to sync state
        if (this.onkeydown && this.onkeyup) {
          this.onkeydown(0xffe5);
          this.onkeyup(0xffe5);
        }
      }
    }

    if (!isUppercase) return this.handleDefaultKey(keysym, event);

    if (shiftHeld || this.capsLockOn) {
      keysym = keysym + 32; // Convert to lowercase keysym
      this.log(
        `[Desktop] Modifier active (${
          shiftHeld ? "Shift" : "CapsLock"
        }), sending lowercase keysym: 0x${keysym.toString(16)}`,
      );
    }

    return this.handleDefaultKey(keysym, event);
  }

  private handleDefaultKey(keysym: number, event: KeydownEvent): KeydownEvent {
    this.pressKey(keysym);
    this.recentKeysyms.set(event.keyCode, keysym);

    if (!event.keyupReliable && !this.isModifierKey(keysym)) {
      this.releaseKey(keysym);
    }

    return event;
  }

  private sendShiftedKey(baseKeysym: number): void {
    if (this.onkeydown) this.onkeydown(0xffe1); // Shift down
    if (this.onkeydown) this.onkeydown(baseKeysym); // Key down
    if (this.onkeyup) this.onkeyup(baseKeysym); // Key up
    if (this.onkeyup) this.onkeyup(0xffe1); // Shift up
  }

  private detectPlatformQuirks(): PlatformQuirks {
    const quirks: PlatformQuirks = {
      keyupUnreliable: false,
      altIsTypableOnly: false,
      capsLockKeyupUnreliable: false,
    };

    if (navigator?.platform) {
      // iOS has unreliable keyup events
      if (navigator.platform.match(/ipad|iphone|ipod/i)) {
        quirks.keyupUnreliable = true;
      }
      // Mac-specific quirks
      else if (navigator.platform.match(/^mac/i)) {
        quirks.altIsTypableOnly = true;
        quirks.capsLockKeyupUnreliable = true;
      }
    }

    return quirks;
  }

  /**
   * Helper to get readable character representation
   */
  private getReadableChar(keysym: number): string {
    // Printable ASCII
    if (keysym >= 0x20 && keysym <= 0x7e) {
      return String.fromCharCode(keysym);
    }

    // Special keys
    return Keyboard.SPECIAL_KEY_NAMES[keysym] || `[0x${keysym.toString(16)}]`;
  }

  /**
   * Check if a keysym represents a modifier key that should not be auto-released
   */
  private isModifierKey(keysym: number): boolean {
    const modifierKeys = new Set([
      0xffe1, // Left Shift
      0xffe2, // Right Shift
      0xffe3, // Left Ctrl
      0xffe4, // Right Ctrl
      0xffe5, // Caps Lock
      0xffe7, // Left Meta
      0xffe8, // Right Meta
      0xffe9, // Left Alt
      0xffea, // Right Alt
      0xffeb, // Left Super/Hyper
      0xffec, // Right Super/Hyper
      0xfe03, // AltGr / ISO Level 3 Shift
    ]);

    return modifierKeys.has(keysym);
  }

  setupEventListeners(): void {
    // Keydown handler
    const { addContextListener, addListener } = this.eventManager;
    addContextListener(
      this.element,
      "keydown",
      (e) => {
        if (!this.onkeydown) return;

        const event = e as KeyboardEvent;
        this.log("[Browser keydown]", {
          key: event.key,
          keyCode: event.keyCode,
          code: event.code,
          shiftKey: event.shiftKey,
          ctrlKey: event.ctrlKey,
          altKey: event.altKey,
          isComposing: event.isComposing || this.isComposing,
        });

        // Ignore composition events
        if (event.isComposing || this.isComposing || event.keyCode === 229) {
          this.log("  ⚠️ Ignoring: composition event");
          return;
        }

        const keydownEvent = new KeydownEvent(event, this.quirks);
        this.eventLog.push(keydownEvent);

        if (this.interpretEvents()) {
          event.preventDefault();
        }
      },
      true,
    );

    // Keypress handler for better character detection
    addContextListener(
      this.element,
      "keypress",
      (e) => {
        if (!this.onkeydown && !this.onkeyup) return;

        const event = e as KeyboardEvent;

        this.log("[Browser keypress]", {
          key: event.key,
          keyCode: event.keyCode,
          charCode: event.charCode,
        });

        const keypressEvent = new KeypressEvent(event);
        this.eventLog.push(keypressEvent);

        if (this.interpretEvents()) {
          event.preventDefault();
        }
      },
      true,
    );

    // Keyup handler
    addContextListener(
      this.element,
      "keyup",
      (e) => {
        if (!this.onkeyup) return;

        const event = e as KeyboardEvent;

        this.log("[Browser keyup]", {
          key: event.key,
          keyCode: event.keyCode,
          code: event.code,
        });

        event.preventDefault();

        const keyupEvent = new KeyupEvent(
          event,
          this.recentKeysyms,
          this.pressed,
        );

        this.eventLog.push(keyupEvent);
        this.interpretEvents();
      },
      true,
    );

    // Composition event handlers for IME support
    addContextListener(this.element, "compositionstart", () => {
      this.log("[Composition] Started");
      this.isComposing = true;
      // Don't reset lastComposedText here - we need it to track across keyboard sessions
    });

    addContextListener(this.element, "compositionend", (e) => {
      const event = e as CompositionEvent;
      this.log("[Composition] Ended, data:", event.data);
      this.isComposing = false;

      if (event.data && this.onkeydown) {
        // Android sends the entire input value in compositionend
        // We need to only type the new characters that weren't sent before

        // Determine what text is new
        let textToType = "";

        if (event.data.startsWith(this.lastComposedText)) {
          // New text appended - only type the difference
          textToType = event.data.substring(this.lastComposedText.length);
          this.log("[Composition] New text appended:", textToType);
        } else if (
          this.lastComposedText &&
          !event.data.includes(this.lastComposedText)
        ) {
          // Text was completely replaced - type the full new text
          textToType = event.data;
          this.log("[Composition] Text replaced, typing full:", textToType);
        } else {
          // Fallback - type everything
          textToType = event.data;
          this.log("[Composition] Typing full text:", textToType);
        }

        if (textToType) {
          this.type(textToType);
        }

        this.lastComposedText = event.data;
      }
    });

    // Input event fallback
    addContextListener(this.element, "input", (e) => {
      if (!this.onkeydown || this.isComposing) return;

      const event = e as InputEvent;
      this.log("[Input event]", {
        data: event.data,
        inputType: event.inputType,
      });

      if (event.data) {
        this.type(event.data);
      }
    });

    // Blur event - partial reset when focus is lost (preserves Caps Lock)
    addContextListener(this.element, "blur", () => {
      this.log("[Blur] Element lost focus, resetting state");
      this.resetOnFocusLoss();
    });

    // Focus event - log for debugging
    addContextListener(this.element, "focus", () => {
      this.log("[Focus] Element gained focus");
    });

    // Visibility change - detect when user switches apps or browser tabs
    addListener(document, "visibilitychange", () => {
      if (document.hidden) {
        this.log("[VisibilityChange] Document hidden, resetting state");
        this.resetOnFocusLoss();
      }
    });

    // Window blur - detect when window loses focus (mobile keyboard close, app switch)
    addListener(window, "blur", () => {
      this.log("[Window Blur] Window lost focus, resetting state");
      this.resetOnFocusLoss();
    });

    // Mobile keyboard detection via viewport resize
    if (this.isMobile) {
      const handleResize = () => {
        const currentHeight =
          window.visualViewport?.height || window.innerHeight;
        const heightDiff = Math.abs(currentHeight - this.lastViewportHeight);

        // If viewport height increased significantly (>100px), keyboard likely closed
        if (currentHeight > this.lastViewportHeight && heightDiff > 100) {
          this.log(
            "[Viewport Resize] Keyboard likely closed (viewport expanded), resetting state",
          );
          this.reset();
        }

        this.lastViewportHeight = currentHeight;
      };

      // Use visualViewport if available (better for mobile)
      if (window.visualViewport) {
        addListener(window.visualViewport, "resize", handleResize);
      } else {
        addListener(window, "resize", handleResize);
      }
    }
  }

  private interpretEvents(): boolean {
    let handledEvent = this.interpretEvent();
    if (!handledEvent) return false;

    let lastEvent: KeyEvent;
    do {
      lastEvent = handledEvent;
      handledEvent = this.interpretEvent();
    } while (handledEvent);

    return (
      lastEvent instanceof KeydownEvent || lastEvent instanceof KeypressEvent
    );
  }

  private interpretEvent(): KeyEvent | null {
    const first = this.eventLog[0];
    if (!first) return null;

    if (first instanceof KeydownEvent) {
      let keysym: number | null = null;
      let acceptedEvents: KeyEvent[] = [];

      // If the event is reliable, use it directly
      if (first.reliable) {
        keysym = first.keysym;
        acceptedEvents = this.eventLog.splice(0, 1);
        this.log("[interpretEvent] Using reliable keydown:", {
          key: first.key,
          keyCode: first.keyCode,
          keysym: keysym?.toString(16),
          char: keysym ? this.getReadableChar(keysym) : "N/A",
        });
      }
      // If followed by keypress, use keypress for printable characters
      else if (this.eventLog[1] instanceof KeypressEvent) {
        keysym = this.eventLog[1].keysym;
        acceptedEvents = this.eventLog.splice(0, 2);
        this.log("[interpretEvent] Using keypress instead of keydown:", {
          keydownKey: first.key,
          keypressKeysym: keysym?.toString(16),
          char: keysym ? this.getReadableChar(keysym) : "N/A",
        });
      }
      // Otherwise, must handle the keydown now
      else if (this.eventLog[1]) {
        keysym = first.keysym;
        acceptedEvents = this.eventLog.splice(0, 1);
        this.log("[interpretEvent] Using keydown (no keypress):", {
          key: first.key,
          keysym: keysym?.toString(16),
          char: keysym ? this.getReadableChar(keysym) : "N/A",
        });
      }

      if (acceptedEvents.length > 0 && keysym) {
        // TV optimization: Fast path for simple characters (a-z, 0-9, space)
        if (this.isTV) {
          const isSimpleChar =
            (keysym >= 0x61 && keysym <= 0x7a) || // a-z
            (keysym >= 0x30 && keysym <= 0x39) || // 0-9
            keysym === 0x20; // space

          if (isSimpleChar) {
            this.pressKey(keysym);
            this.recentKeysyms.set(first.keyCode, keysym);
            if (!first.keyupReliable && !this.isModifierKey(keysym)) {
              this.releaseKey(keysym);
            }
            return first;
          }
        }

        // On mobile, ignore CapsLock completely - don't send it to server
        if (this.isMobile && keysym === 0xffe5) {
          this.log("[interpretEvent] Mobile: ignoring CapsLock key");
          return first;
        }

        // Use specialized handlers for better performance
        return this.handleCharacterEvent(keysym, first);
      }
    } else if (first instanceof KeyupEvent && !this.quirks.keyupUnreliable) {
      const keysym = first.keysym;
      if (keysym) {
        this.log("[interpretEvent] Keyup:", {
          key: first.key,
          keysym: keysym.toString(16),
          char: this.getReadableChar(keysym),
        });
        this.releaseKey(keysym);
        this.recentKeysyms.delete(first.keyCode);
      }
      return this.eventLog.shift() || null;
    } else if (first instanceof KeyupEvent && this.quirks.keyupUnreliable) {
      // On mobile with unreliable keyup, we need to handle modifier keys specially
      const keysym = first.keysym;

      // For modifier keys, we should still process the keyup even though it's unreliable
      if (keysym && this.isModifierKey(keysym)) {
        // On mobile, ignore CapsLock completely
        if (this.isMobile && keysym === 0xffe5) {
          this.log("[interpretEvent] Mobile: ignoring CapsLock keyup");
          return this.eventLog.shift() || null;
        }

        this.log(
          "[interpretEvent] Keyup (unreliable platform, but modifier):",
          {
            key: first.key,
            keysym: keysym.toString(16),
            char: this.getReadableChar(keysym),
          },
        );

        // Just release the modifier normally
        this.releaseKey(keysym);
        this.recentKeysyms.delete(first.keyCode);
      }

      return this.eventLog.shift() || null;
    } else {
      // Remove unhandleable events
      return this.eventLog.shift() || null;
    }

    return null;
  }

  private pressKey(keysym: number): void {
    if (this.pressed.has(keysym)) return;

    this.pressed.add(keysym);

    this.log("[pressKey] Sending to server:", {
      keysym: "0x" + keysym.toString(16),
      decimal: keysym,
      char: this.getReadableChar(keysym),
      currentlyPressed: Array.from(this.pressed).map((k) => ({
        keysym: "0x" + k.toString(16),
        char: this.getReadableChar(k),
      })),
    });

    if (this.onkeydown) {
      this.onkeydown(keysym);
    }

    // CapsLock is a toggle key - also send immediate keyup to complete the toggle
    if (keysym === 0xffe5) {
      this.log("[pressKey] CapsLock toggle - sending immediate keyup");
      if (this.onkeyup) {
        this.onkeyup(keysym);
      }
      this.capsLockOn = !this.capsLockOn;
      this.log(`[CapsLock] State: ${this.capsLockOn ? "ON" : "OFF"}`);
    }

    // Set up key repeat for non-modifier keys
    if (!this.noRepeatKeys.has(keysym)) {
      this.clearKeyRepeat();
      const repeatInterval = this.isTV ? 100 : 50; // Slower repeat for TV performance
      this.keyRepeatTimeout = window.setTimeout(() => {
        this.keyRepeatInterval = window.setInterval(() => {
          if (this.onkeydown) {
            this.log("[keyRepeat] Repeating:", {
              keysym: "0x" + keysym.toString(16),
              char: this.getReadableChar(keysym),
            });
            this.onkeydown(keysym);
          }
        }, repeatInterval);
      }, 500);
    }
  }

  private releaseKey(keysym: number): void {
    if (!this.pressed.has(keysym)) return;

    this.pressed.delete(keysym);
    this.clearKeyRepeat();

    this.log("🔼 [releaseKey] Releasing:", {
      keysym: "0x" + keysym.toString(16),
      char: this.getReadableChar(keysym),
      remainingPressed: Array.from(this.pressed).map((k) => ({
        keysym: "0x" + k.toString(16),
        char: this.getReadableChar(k),
      })),
    });

    // CapsLock is a toggle - send another toggle to turn it back off
    if (keysym === 0xffe5) {
      this.log(
        "[releaseKey] CapsLock toggle - sending keydown+keyup to toggle OFF",
      );
      if (this.onkeydown) {
        this.onkeydown(keysym);
      }
      if (this.onkeyup) {
        this.onkeyup(keysym);
      }
      this.capsLockOn = !this.capsLockOn;
      this.log(`[CapsLock] State: ${this.capsLockOn ? "ON" : "OFF"}`);
    } else if (this.onkeyup) {
      // Normal key - just send keyup
      this.onkeyup(keysym);
    }
  }

  private clearKeyRepeat(): void {
    if (this.keyRepeatTimeout) {
      clearTimeout(this.keyRepeatTimeout);
      this.keyRepeatTimeout = null;
    }
    if (this.keyRepeatInterval) {
      clearInterval(this.keyRepeatInterval);
      this.keyRepeatInterval = null;
    }
  }

  /**
   * Helper to convert codepoint to keysym
   */
  private keysymFromCharcode(codepoint: number): number {
    // Control characters
    if (codepoint <= 0x1f || (codepoint >= 0x7f && codepoint <= 0x9f)) {
      return 0xff00 | codepoint;
    }

    // ASCII characters
    if (codepoint >= 0x0000 && codepoint <= 0x00ff) {
      return codepoint;
    }

    // Unicode characters
    if (codepoint >= 0x0100 && codepoint <= 0x10ffff) {
      return 0x01000000 | codepoint;
    }

    return codepoint;
  }

  /**
   * Types a string by pressing and releasing keys for each character
   */
  public type(text: string): void {
    this.log("[type] Typing text:", text);

    // Debounce rapid duplicate typing on TV platforms to prevent special character issues
    if (this.isTV) {
      const now = Date.now();
      if (
        text === this.lastTypedText &&
        now - this.lastTypeTime < this.typeDebounceMs
      ) {
        this.log(
          `[type] TV Platform: Ignoring duplicate text "${text}" within ${this.typeDebounceMs}ms`,
        );
        return;
      }
      this.lastTypedText = text;
      this.lastTypeTime = now;
    }

    for (let i = 0; i < text.length; i++) {
      const char = text[i];

      // TV optimization: Fast path for simple lowercase characters
      if (this.isTV && char >= "a" && char <= "z") {
        const keysym = char.charCodeAt(0);
        if (this.onkeydown) this.onkeydown(keysym);
        if (this.onkeyup) this.onkeyup(keysym);
        continue;
      }

      // Check if this is an uppercase letter (A-Z)
      if (char >= "A" && char <= "Z") {
        // Check if Shift or CapsLock is already pressed
        const shiftAlreadyPressed =
          this.pressed.has(0xffe1) || this.pressed.has(0xffe2);
        const capsLockPressed = this.pressed.has(0xffe5);

        const lowercaseKeysym = char.toLowerCase().charCodeAt(0);

        if (shiftAlreadyPressed || capsLockPressed) {
          // Shift or CapsLock is active - just send the lowercase key
          this.log(
            `  [type] Uppercase '${char}': ${
              shiftAlreadyPressed ? "Shift" : "CapsLock"
            } already active, sending '${char.toLowerCase()}'(0x${lowercaseKeysym.toString(
              16,
            )})`,
          );

          if (this.onkeydown) {
            this.onkeydown(lowercaseKeysym);
          }
          if (this.onkeyup) {
            this.onkeyup(lowercaseKeysym);
          }
        } else {
          // No modifiers - artificially press and release Shift
          this.log(
            `  [type] Uppercase '${char}': Artificially pressing Shift(0xffe1) + '${char.toLowerCase()}'(0x${lowercaseKeysym.toString(
              16,
            )})`,
          );

          // Press Shift
          if (this.onkeydown) {
            this.onkeydown(0xffe1);
          }

          // Press lowercase key
          if (this.onkeydown) {
            this.onkeydown(lowercaseKeysym);
          }

          // Release lowercase key
          if (this.onkeyup) {
            this.onkeyup(lowercaseKeysym);
          }

          // Release Shift
          if (this.onkeyup) {
            this.onkeyup(0xffe1);
          }
        }
      } else {
        // Check if this is a special character that requires Shift+number combination
        const keysym = this.keysymFromCharcode(char.charCodeAt(0));

        const isSpecialCharacter = Object.prototype.hasOwnProperty.call(
          Keyboard.SPECIAL_CHARACTERS,
          keysym,
        );

        if (isSpecialCharacter) {
          const baseKeysym = Keyboard.SPECIAL_CHARACTERS[keysym];

          this.log(
            `[type] Character '${char}': keysym 0x${keysym.toString(
              16,
            )}, isSpecialCharacter: ${isSpecialCharacter}, baseKeysym: 0x${baseKeysym.toString(
              16,
            )}, unicode symbol: ${(baseKeysym & 0xff000000) === 0x01000000}`,
          );

          if ((baseKeysym & 0xff000000) === 0x01000000) {
            // Unicode symbol — inject via clipboard if available, else direct keysym
            this.log(
              `  [type] Unicode symbol '${char}': 0x${baseKeysym.toString(16)}`,
            );
            if (this.onunicode) {
              this.onunicode(char);
            } else {
              this.pressKey(baseKeysym);
              this.releaseKey(baseKeysym);
            }
          } else {
            this.log(
              `  [type] Special character '${char}': Artificially pressing Shift(0xffe1) + base key (0x${baseKeysym.toString(
                16,
              )})`,
            );

            // Press Shift
            if (this.onkeydown) {
              this.onkeydown(0xffe1);
            }

            // Press base key
            if (this.onkeydown) {
              this.onkeydown(baseKeysym);
            }

            // Release base key
            if (this.onkeyup) {
              this.onkeyup(baseKeysym);
            }

            // Release Shift
            if (this.onkeyup) {
              this.onkeyup(0xffe1);
            }
          }
        } else {
          // For all other characters: send as-is
          this.log(`  [type] Character '${char}': 0x${keysym.toString(16)}`);

          this.pressKey(keysym);
          this.releaseKey(keysym);
        }
      }
    }
  }

  /**
   * Resets the keyboard state, releasing all pressed keys except Caps Lock
   */
  private resetOnFocusLoss(): void {
    this.log(
      "[resetOnFocusLoss] Resetting keyboard state (preserving Caps Lock)",
      {
        currentlyPressed: Array.from(this.pressed).map((k) => ({
          keysym: "0x" + k.toString(16),
          char: this.getReadableChar(k),
        })),
        capsLockState: this.capsLockOn ? "ON" : "OFF",
      },
    );

    // Release pressed keys but skip toggle keys like Caps Lock
    const pressedKeys = Array.from(this.pressed);
    for (const keysym of pressedKeys) {
      // Skip Caps Lock and other toggle keys - don't send release events for them
      if (keysym === 0xffe5) {
        // Caps Lock
        this.log(
          "[resetOnFocusLoss] Skipping Caps Lock release to preserve toggle state",
        );
        this.pressed.delete(keysym); // Just remove from pressed set
        continue;
      }
      this.releaseKey(keysym);
    }

    this.eventLog = [];
    this.recentKeysyms.clear();
    this.clearKeyRepeat();
  }

  /**
   * Full reset for new sessions - resets everything including toggle states
   */
  public reset(): void {
    this.log("[reset] Resetting keyboard state", {
      currentlyPressed: Array.from(this.pressed).map((k) => ({
        keysym: "0x" + k.toString(16),
        char: this.getReadableChar(k),
      })),
    });

    // Release all pressed keys
    const pressedKeys = Array.from(this.pressed);
    for (const keysym of pressedKeys) {
      this.releaseKey(keysym);
    }

    this.eventLog = [];
    this.recentKeysyms.clear();
    this.clearKeyRepeat();
    this.capsLockOn = false;
    // Don't reset lastComposedText - Android preserves input text across keyboard close/reopen
  }

  /**
   * Manually press a key (useful for programmatic input)
   */
  public press(keysym: number): void {
    this.log("[press] Manual key press:", {
      keysym: "0x" + keysym.toString(16),
      char: this.getReadableChar(keysym),
    });
    this.pressKey(keysym);
  }

  /**
   * Manually release a key
   */
  public release(keysym: number): void {
    this.log("[release] Manual key release:", {
      keysym: "0x" + keysym.toString(16),
      char: this.getReadableChar(keysym),
    });
    this.releaseKey(keysym);
  }

  /**
   * Get currently pressed keys
   */
  public getPressedKeys(): number[] {
    return Array.from(this.pressed);
  }

  /**
   * Cleanup method - remove all event listeners
   * Call this when the keyboard instance is no longer needed
   */
  public destroy(): void {
    this.log("[destroy] Cleaning up keyboard instance");
    this.reset();
    this.eventManager.removeAllListeners();
  }
}
